import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import NavUser from "../components/NavUser";
import "./BookingDetails.css";

const API = process.env.REACT_APP_API_URL;

export default function BookingDetailsPage() {
    const { bookingId } = useParams();
    const navigate = useNavigate();
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!bookingId) return;

        const fetchBooking = async () => {
            try {
                setLoading(true);
                // Assuming there's an endpoint to get a single booking by ID.
                // If not, we might need to adjust this. 
                // Based on typical REST patterns: GET /bookings/:id
                const res = await fetch(`${API}/bookings/${bookingId}`);
                if (!res.ok) {
                    // Fallback: Try fetching from hotel owner bookings if generic endpoint fails, 
                    // though a specific endpoint is better. For now, assuming standard endpoint.
                    throw new Error("Failed to fetch booking details.");
                }
                const data = await res.json();
                setBooking(data);
            } catch (err) {
                console.error(err);
                setError("Could not load booking details.");
            } finally {
                setLoading(false);
            }
        };

        fetchBooking();
    }, [bookingId]);

    if (loading) {
        return (
            <div className="booking-details-page">
                <NavUser />
                <div className="bd-container">
                    <div className="bd-loading">Loading booking details...</div>
                </div>
            </div>
        );
    }

    if (error || !booking) {
        return (
            <div className="booking-details-page">
                <NavUser />
                <div className="bd-container">
                    <div className="bd-error">
                        <h3>Error</h3>
                        <p>{error || "Booking not found."}</p>
                        <button className="bd-btn" onClick={() => navigate(-1)}>Go Back</button>
                    </div>
                </div>
            </div>
        );
    }

    const {
        user,
        listing,
        checking_date,
        checkout_date,
        price,
        booking_status_id,
        number_of_guests,
        pickup_requested,
        special_requests,
        coins_used_amount,
        price_after_discount
    } = booking;

    // Status Badge Helper
    const getStatusBadge = (statusId) => {
        switch (statusId) {
            case 1: return <span className="bd-badge pending">Pending</span>;
            case 2: return <span className="bd-badge confirmed">Confirmed</span>;
            case 3: return <span className="bd-badge cancelled">Cancelled</span>;
            case 4: return <span className="bd-badge completed">Completed</span>;
            default: return <span className="bd-badge default">Unknown</span>;
        }
    };

    return (
        <div className="booking-details-page">
            <NavUser />
            <div className="bd-container fade-in">
                <div className="bd-header">
                    <button className="bd-back-btn" onClick={() => navigate(-1)}>← Back</button>
                    <h1 className="bd-title">Booking #{bookingId}</h1>
                    <div className="bd-status">{getStatusBadge(booking_status_id)}</div>
                </div>

                <div className="bd-grid">
                    {/* Main Info Card */}
                    <div className="bd-card">
                        <h2 className="bd-card-title">Booking Summary</h2>

                        <div className="bd-row">
                            <div className="bd-col">
                                <span className="bd-label">Check-in</span>
                                <span className="bd-value">{new Date(checking_date).toLocaleDateString()}</span>
                            </div>
                            <div className="bd-col">
                                <span className="bd-label">Check-out</span>
                                <span className="bd-value">{new Date(checkout_date).toLocaleDateString()}</span>
                            </div>
                        </div>

                        <div className="bd-row">
                            <div className="bd-col">
                                <span className="bd-label">Guests</span>
                                <span className="bd-value">{number_of_guests}</span>
                            </div>
                            <div className="bd-col">
                                <span className="bd-label">Pickup Requested</span>
                                <span className="bd-value">{pickup_requested ? "Yes" : "No"}</span>
                            </div>
                        </div>

                        <div className="bd-full-row">
                            <span className="bd-label">Special Requests</span>
                            <p className="bd-text">{special_requests || "None"}</p>
                        </div>
                    </div>

                    {/* Traveler Info */}
                    <div className="bd-card">
                        <h2 className="bd-card-title">Traveler Information</h2>
                        <div className="bd-profile">
                            <div className="bd-avatar">
                                {user?.full_name?.charAt(0) || "U"}
                            </div>
                            <div>
                                <h3 className="bd-profile-name">{user?.full_name}</h3>
                                <p className="bd-profile-email">{user?.email}</p>
                                <p className="bd-profile-phone">{user?.phone_number || "No phone number"}</p>
                            </div>
                        </div>
                    </div>

                    {/* Hotel/Room Info */}
                    <div className="bd-card">
                        <h2 className="bd-card-title">Property Details</h2>
                        <h3 className="bd-property-name">{listing?.name}</h3>
                        <p className="bd-property-loc">{listing?.location}</p>
                        <p className="bd-property-type">{listing?.property_type?.name}</p>
                    </div>

                    {/* Payment Info */}
                    <div className="bd-card">
                        <h2 className="bd-card-title">Payment Details</h2>

                        <div className="bd-summary-row">
                            <span>Base Price</span>
                            <span>Rs. {price}</span>
                        </div>

                        {coins_used_amount > 0 && (
                            <div className="bd-summary-row discount">
                                <span>Coins Used</span>
                                <span>- Rs. {coins_used_amount}</span>
                            </div>
                        )}

                        <div className="bd-summary-row total">
                            <span>Total Paid</span>
                            <span>Rs. {price_after_discount || price}</span>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
